<template>
    <div class="rounded-[6.75px] border-[1px] border-[#DDE0E5] bg-[#F9FAFB]">
        <button
            type="button"
            class="flex w-full cursor-pointer items-center justify-between px-[14px] py-[14px]"
            @click="toggleEmailInstructions"
        >
            <div class="text-[14px] font-medium leading-[142%] text-[#161E2E]">
                {{ t(titleKey) }}
            </div>

            <span
                class="text-[10.5px] font-normal leading-[142%] text-[#161E2E]"
            >
                {{
                    emailInstructionsOpen ? t(openLabelKey) : t(closedLabelKey)
                }}
            </span>
        </button>

        <div
            v-if="emailInstructionsOpen"
            class="border-t-[1px] border-[#E5E7EB] px-[14px] pb-[14px]"
        >
            <div class="mt-[15.5px] flex flex-col gap-[15.5px]">
                <div>
                    <label
                        class="block text-[12px] font-medium leading-[140%]"
                        style="color: #000000"
                    >
                        {{ t("vue.domain_wizard_send_to_email") }}
                    </label>

                    <input
                        v-model.trim="emailTo"
                        type="email"
                        :placeholder="t('vue.domain_wizard_email_placeholder')"
                        class="mt-2 h-10 w-full rounded-md border border-gray-200 bg-white px-3 text-sm text-gray-900 placeholder:text-gray-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:placeholder-transparent"
                        @blur="emailToTouched = true"
                    />

                    <p
                        v-if="emailToTouched && emailToError"
                        class="mt-2 text-xs text-red-600"
                    >
                        {{ emailToError }}
                    </p>
                </div>

                <div>
                    <label
                        class="block text-[12px] font-medium leading-[140%]"
                        style="color: #000000"
                    >
                        {{ t("vue.domain_wizard_message_optional") }}
                    </label>

                    <textarea
                        v-model="emailMessage"
                        rows="9"
                        class="mt-2 w-full resize-y rounded-md border border-gray-200 bg-white p-3 text-[12px] leading-[140%] text-gray-800 placeholder:text-gray-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                    />

                    <p
                        v-if="emailStatusText"
                        class="mt-2 text-xs"
                        :class="emailStatusClass"
                    >
                        {{ emailStatusText }}
                    </p>
                </div>

                <div class="flex justify-center">
                    <button
                        type="button"
                        class="inline-flex h-[31.5px] cursor-pointer items-center justify-center rounded-md bg-[#088BEA] px-4 text-[11px] font-medium text-white disabled:opacity-50"
                        :disabled="emailSending || !canSendEmail"
                        @click="sendEmail"
                    >
                        {{
                            emailSending
                                ? t("vue.domain_wizard_sending")
                                : t("vue.domain_wizard_send_email")
                        }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import axios from "axios";
import route from "ziggy-js";
import { computed, ref, watch } from "vue";
import { create } from "@/composables/useNotifications";
import { useTranslations } from "@/composables/useTranslations";

type EmailInstructionsProps = {
    domain?: string | null;
    txtRecord?: string | null;
    titleKey?: string;
    bodyKey?: string;
    exampleDomainKey?: string;
    openLabelKey?: string;
    closedLabelKey?: string;
    routeName?: string;
};

const props = withDefaults(defineProps<EmailInstructionsProps>(), {
    domain: "",
    txtRecord: "",
    titleKey: "vue.domain_wizard_email_instructions",
    bodyKey: "vue.domain_wizard_email_body",
    exampleDomainKey: "vue.domain_wizard_example_domain",
    openLabelKey: "vue.cancel",
    closedLabelKey: "vue.show_details",
    routeName: "domainGuide.sendWizardSetEmail",
});

const { t } = useTranslations();

const emailInstructionsOpen = ref(false);
const emailTo = ref("");
const emailMessage = ref("");
const emailError = ref<string | null>(null);
const emailSent = ref(false);
const emailSending = ref(false);
const emailToTouched = ref(false);
const emailMessageDirty = ref(false);
const suppressEmailDirty = ref(false);

const emailToError = computed(() => {
    if (!emailTo.value.trim()) return t("vue.domain_wizard_email_required");
    const v = emailTo.value.trim().toLowerCase();
    const ok = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
    return ok ? "" : t("vue.domain_wizard_email_invalid");
});

const canSendEmail = computed(() => !emailToError.value);

const emailStatusText = computed(() => {
    if (emailError.value) return emailError.value;
    if (emailSent.value) return t("vue.domain_wizard_email_queued");
    return "";
});

const emailStatusClass = computed(() => {
    if (emailError.value) return "text-red-600";
    if (emailSent.value) return "text-green-700";
    return "text-gray-500";
});

const buildEmailMessage = () => {
    const d = props.domain?.trim() || t(props.exampleDomainKey);
    return t(props.bodyKey, {
        domain: d,
        txtRecord: props.txtRecord ?? "",
    });
};

function resetEmailForm() {
    emailError.value = null;
    emailSent.value = false;
    emailToTouched.value = false;

    emailTo.value = "";
    emailMessageDirty.value = false;
    suppressEmailDirty.value = true;
    emailMessage.value = buildEmailMessage();
}

function toggleEmailInstructions() {
    emailInstructionsOpen.value = !emailInstructionsOpen.value;
    if (emailInstructionsOpen.value) {
        resetEmailForm();
    }
}

watch(
    () => [props.domain, props.txtRecord],
    () => {
        if (!emailMessageDirty.value) {
            suppressEmailDirty.value = true;
            emailMessage.value = buildEmailMessage();
        }
    },
    { immediate: true },
);

watch(emailMessage, () => {
    if (suppressEmailDirty.value) {
        suppressEmailDirty.value = false;
        return;
    }

    emailMessageDirty.value = true;
});

async function sendEmail() {
    emailError.value = null;
    emailSent.value = false;

    if (!canSendEmail.value) {
        emailToTouched.value = true;
        return;
    }

    emailSending.value = true;

    try {
        const payload = {
            to: emailTo.value.trim(),
            message: emailMessage.value,
        };

        const response = await axios.post(route(props.routeName), payload, {
            headers: { Accept: "application/json" },
        });

        const { status, message } = response.data ?? {};

        if (status === "error") {
            create({
                type: "error",
                title: message || t("vue.domain_wizard_email_send_failed"),
            });
            emailError.value =
                message || t("vue.domain_wizard_email_send_failed");
            return;
        }

        if (status === "ok") {
            create({
                type: "success",
                title: t("vue.domain_wizard_email_sent"),
            });
            emailSent.value = true;
        }
    } catch (error) {
        emailError.value = t("vue.domain_wizard_email_send_failed");
    } finally {
        emailSending.value = false;
    }
}
</script>
